import React, { Component, Fragment } from 'react';
import * as Sentry from '@sentry/react';
import { Paragraph } from '../typography/typography';
import Icon from '../icon/icons';

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      error: null,
      errorInfo: null,
      hasError: false,
      remountKey: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { 
      error: error,
      hasError: true 
    };
  }

  componentDidCatch(error, errorInfo) {
    console.group('🚨 Error Boundary Caught Error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();

    Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });

    this.setState({
      errorInfo: errorInfo,
    });
  }

  handleReload = () => {
    // Remount child subtree instead of full page reload
    this.setState((prev) => ({
      error: null,
      errorInfo: null,
      hasError: false,
      remountKey: prev.remountKey + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[200px] w-full flex-col flex items-center justify-center p-6">
          <div className="flex items-center gap-2 text-gray-700 dark:text-grey-300">
            <Paragraph size="base" className="m-0">Something went wrong.</Paragraph>
            <button
              type="button"
              aria-label="Reload"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center text-primary hover:text-primary-800"
            >
              <Icon name="reload" iconClass="w-5 h-5" />
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="mt-6 text-left w-full max-w-xl">
              <summary className="cursor-pointer text-center text-sm text-gray-500 dark:text-grey-400 hover:text-gray-700 dark:hover:text-grey-300">
                Show Error Details (Development)
              </summary>
              <div className="mt-2 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-xs font-mono">
                <div className="mb-2">
                  <strong>Error:</strong> {this.state.error.toString()}
                </div>
                {this.state.errorInfo && (
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="whitespace-pre-wrap mt-1 text-xs">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}
        </div>
      );
    }

    return (
      <Fragment key={this.state.remountKey}>
        {this.props.children}
      </Fragment>
    );
  }
}

export default ErrorBoundary;